import progressionData from '../../../shared/guild-progression.json';

export type GuildRank = 'G' | 'F' | 'E' | 'D' | 'C' | 'B' | 'A' | 'S' | 'SS' | 'SSS';

export interface GuildRankDefinition {
  rank: GuildRank;
  minReputation: number;
  nextReputation: number;
  label: string;
  color: string;
}

export const PLAYER_LEVEL_MAX = progressionData.playerLevelMax;
export const LEVELS_PER_CERTIFICATION = progressionData.levelsPerCertification;
export const GUILD_RANKS = progressionData.ranks as GuildRankDefinition[];
export const GUILD_RANK_ORDER = GUILD_RANKS.map((entry) => entry.rank);

export function isGuildRank(value: unknown): value is GuildRank {
  return typeof value === 'string' && GUILD_RANK_ORDER.includes(value as GuildRank);
}

export function rankForReputation(reputation: number): GuildRankDefinition {
  const value = Math.max(0, Math.floor(Number(reputation) || 0));
  let current = GUILD_RANKS[0]!;
  for (const definition of GUILD_RANKS) {
    if (value >= definition.minReputation) current = definition;
  }
  return current;
}

export function certificationMaxLevel(rank: GuildRank | null | undefined): number {
  const index = Math.max(0, GUILD_RANK_ORDER.indexOf(isGuildRank(rank) ? rank : 'G'));
  return Math.min(PLAYER_LEVEL_MAX, (index + 1) * LEVELS_PER_CERTIFICATION);
}

export function xpNeededForLevel(level: number): number {
  return Math.floor(Math.pow(Math.max(1, Number(level) || 1), 1.5) * 100);
}

export function maximumStoredXpForLevel(level: number, certificationRank: GuildRank | null | undefined): number {
  const safeLevel = Math.max(1, Math.min(PLAYER_LEVEL_MAX, Math.floor(Number(level) || 1)));
  if (safeLevel >= PLAYER_LEVEL_MAX) return 0;
  const needed = xpNeededForLevel(safeLevel);
  return safeLevel === certificationMaxLevel(certificationRank) ? needed : Math.max(0, needed - 1);
}

/** Certification minimale permettant de conserver un niveau déjà atteint. */
export function minimumCertificationForLevel(level: number): GuildRank {
  const safeLevel = Math.max(1, Math.min(PLAYER_LEVEL_MAX, Math.floor(Number(level) || 1)));
  const index = Math.min(GUILD_RANK_ORDER.length - 1, Math.ceil(safeLevel / LEVELS_PER_CERTIFICATION) - 1);
  return GUILD_RANK_ORDER[index]!;
}

export function certificationForItemLevel(itemLevel: number): GuildRank {
  return minimumCertificationForLevel(itemLevel);
}

export function rankAtLeast(actual: GuildRank | null | undefined, required: GuildRank): boolean {
  const actualIndex = GUILD_RANK_ORDER.indexOf(isGuildRank(actual) ? actual : 'G');
  return actualIndex >= GUILD_RANK_ORDER.indexOf(required);
}

export function nextGuildRank(rank: GuildRank | null | undefined): GuildRank | null {
  const index = GUILD_RANK_ORDER.indexOf(isGuildRank(rank) ? rank : 'G');
  return GUILD_RANK_ORDER[index + 1] || null;
}
